import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  advertiserApi,
  type AudienceInsightsReport,
  type CaliberDemandReport,
  type InventoryGapsReport,
  type ReportEnvelope,
  type SearchDemandReport,
} from '../../services/advertiserApi';

type ReportKey = 'search-demand' | 'caliber-demand' | 'inventory-gaps' | 'audience-insights';

interface TabSpec {
  key: ReportKey;
  label: string;
}

const TAB_SPECS: TabSpec[] = [
  { key: 'search-demand', label: 'Search demand' },
  { key: 'caliber-demand', label: 'Caliber demand' },
  { key: 'inventory-gaps', label: 'Inventory gaps' },
  { key: 'audience-insights', label: 'Audience insights' },
];

/**
 * Advertiser market-intelligence reports — tabbed view of the four
 * §11.5 reports. Each tab fetches its own report independently so
 * advertisers can drill into one without paying the cost of the
 * others. All data is aggregated + small-cohort suppressed at the
 * API layer (see api/src/services/advertiserReports.js); this page
 * surfaces `suppressedCohortCount` as a privacy notice.
 */
export function ReportsPage() {
  const [active, setActive] = useState<ReportKey>('search-demand');

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Market intelligence reports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Aggregated, privacy-preserving views of demand, supply, and audience signals. Cohorts below
        the suppression threshold are hidden so individual users cannot be inferred.
      </Typography>

      <Tabs
        value={active}
        onChange={(_e, value) => setActive(value as ReportKey)}
        aria-label="Report selector"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {TAB_SPECS.map((spec) => (
          <Tab key={spec.key} value={spec.key} label={spec.label} />
        ))}
      </Tabs>

      <Box role="tabpanel" aria-label={`${active} report`}>
        {active === 'search-demand' ? <SearchDemandTab /> : null}
        {active === 'caliber-demand' ? <CaliberDemandTab /> : null}
        {active === 'inventory-gaps' ? <InventoryGapsTab /> : null}
        {active === 'audience-insights' ? <AudienceInsightsTab /> : null}
      </Box>
    </Box>
  );
}

// ── Per-report tab components ──────────────────────────────────────────────

function SearchDemandTab() {
  const query = useQuery({
    queryKey: ['advertiser', 'reports', 'search-demand'],
    queryFn: () => advertiserApi.getSearchDemandReport(),
  });
  return (
    <ReportFrame query={query} pickReport={(d) => d.report}>
      {(report) => (
        <ReportTable
          ariaLabel="Search demand rows"
          headers={['Caliber', 'Region', 'Alert count', 'Watchlist count']}
          rows={report.data.rows.map((r) => [
            r.caliber,
            r.region,
            String(r.alertCount),
            String(r.watchlistCount),
          ])}
        />
      )}
    </ReportFrame>
  );
}

function CaliberDemandTab() {
  const query = useQuery({
    queryKey: ['advertiser', 'reports', 'caliber-demand'],
    queryFn: () => advertiserApi.getCaliberDemandReport(),
  });
  return (
    <ReportFrame query={query} pickReport={(d) => d.report}>
      {(report) => (
        <ReportTable
          ariaLabel="Caliber demand rows"
          headers={['Caliber', 'Region', 'Current count', '7d ago', 'Note']}
          rows={report.data.rows.map((r) => [
            r.caliber,
            r.region,
            String(r.currentCount),
            r.sevenDaysAgoCount == null ? '—' : String(r.sevenDaysAgoCount),
            r.trendNote,
          ])}
        />
      )}
    </ReportFrame>
  );
}

function InventoryGapsTab() {
  const query = useQuery({
    queryKey: ['advertiser', 'reports', 'inventory-gaps'],
    queryFn: () => advertiserApi.getInventoryGapsReport(),
  });
  return (
    <ReportFrame query={query} pickReport={(d) => d.report}>
      {(report) => (
        <ReportTable
          ariaLabel="Inventory gap rows"
          headers={['Caliber', 'Region', 'Alerts', 'In-stock listings', 'Gap']}
          rows={report.data.rows.map((r) => [
            r.caliber,
            r.region,
            String(r.alertCount),
            String(r.inStockListingCount),
            String(r.gap),
          ])}
        />
      )}
    </ReportFrame>
  );
}

function AudienceInsightsTab() {
  const query = useQuery({
    queryKey: ['advertiser', 'reports', 'audience-insights'],
    queryFn: () => advertiserApi.getAudienceInsightsReport(),
  });
  return (
    <ReportFrame query={query} pickReport={(d) => d.report}>
      {(report) => (
        <ReportTable
          ariaLabel="Audience insight rows"
          headers={['Use case', 'Region', 'Alert count']}
          rows={report.data.rows.map((r) => [r.useCase, r.region, String(r.alertCount)])}
        />
      )}
    </ReportFrame>
  );
}

// ── Shared scaffolding ─────────────────────────────────────────────────────

type AnyReport =
  | SearchDemandReport
  | CaliberDemandReport
  | InventoryGapsReport
  | AudienceInsightsReport;

interface ReportFrameProps<D, R extends AnyReport> {
  query: {
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    data: D | undefined;
  };
  pickReport: (data: D) => R;
  children: (report: R) => React.ReactNode;
}

/**
 * Wraps the loading / error / empty / suppression-notice scaffolding
 * shared by every report tab.
 */
function ReportFrame<D, R extends AnyReport>({
  query,
  pickReport,
  children,
}: ReportFrameProps<D, R>) {
  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading report" />
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error" role="alert">
        Failed to load report: {(query.error as Error).message}
      </Alert>
    );
  }
  if (!query.data) {
    return (
      <Alert severity="info" role="status">
        No data yet.
      </Alert>
    );
  }
  const report = pickReport(query.data);
  return (
    <>
      <SuppressionNotice report={report} />
      {report.data.rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">No rows in this report yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The MVP report aggregates active alerts, watchlist items, and listings — counts will
            appear here as user activity accrues.
          </Typography>
        </Paper>
      ) : (
        children(report)
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Generated {report.generatedAt} · Source: {report.source}
      </Typography>
    </>
  );
}

function SuppressionNotice({ report }: { report: ReportEnvelope<unknown> }) {
  const n = report.data.suppressedCohortCount;
  if (!n) return null;
  return (
    <Alert severity="info" role="status" sx={{ mb: 2 }}>
      {n} cohort{n === 1 ? '' : 's'} hidden due to small-cohort suppression (threshold:{' '}
      {report.data.opts.minCohort}).
    </Alert>
  );
}

interface ReportTableProps {
  ariaLabel: string;
  headers: string[];
  rows: string[][];
}

function ReportTable({ ariaLabel, headers, rows }: ReportTableProps) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, idx) => (
            <TableRow key={idx}>
              {cells.map((cell, cellIdx) => (
                <TableCell key={cellIdx}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ReportsPage;
